import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../utils/apiClient';
import { useVendor } from '../contexts/VendorContext';

export interface Category {
  id: number;
  name: string;
  on_store?: boolean;
  is_active?: boolean;
}

let cache: Category[] | null = null;
let inflight: Promise<Category[]> | null = null;

export async function loadCategories(force = false): Promise<Category[]> {
  if (cache && !force) return cache;
  if (!inflight) {
    inflight = apiFetch('/api/categories/')
      .then((r) => (r.ok ? r.json() : []))
      .then((list: Category[]) => {
        cache = list;
        return list;
      })
      .catch(() => cache || [])
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

export function invalidateCategories() {
  cache = null;
}

/** Split a comma-separated categories string into trimmed names. */
export function splitCategories(value: string | null | undefined): string[] {
  return (value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface Props {
  /** Comma-separated value, as stored on the book */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
}

/**
 * Category chooser: chips loaded from the storefront's collections.
 * Values not in the list (e.g. from the ISBN lookup) are shown as extra
 * chips so nothing is silently dropped. Admins can add a category inline,
 * which also creates the collection on the store.
 */
const CategoryPicker: React.FC<Props> = ({ value, onChange, label = 'Categories', hint }) => {
  const { isAdmin } = useVendor();
  const [options, setOptions] = useState<Category[]>(cache || []);
  const [loading, setLoading] = useState(!cache);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories().then((list) => {
      setOptions(list);
      setLoading(false);
    });
  }, []);

  const selected = useMemo(() => splitCategories(value), [value]);
  const selectedLower = useMemo(() => new Set(selected.map((s) => s.toLowerCase())), [selected]);
  const optionNames = new Set(options.map((o) => o.name.toLowerCase()));
  const extras = selected.filter((s) => !optionNames.has(s.toLowerCase()));

  const toggle = (name: string) => {
    const lower = name.toLowerCase();
    const next = selectedLower.has(lower) ? selected.filter((s) => s.toLowerCase() !== lower) : [...selected, name];
    onChange(next.join(', '));
  };

  const addCategory = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    setAddError('');
    try {
      const r = await apiFetch('/api/admin/categories/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.name?.[0] || data.error || 'Could not add the category.');
      invalidateCategories();
      const list = await loadCategories(true);
      setOptions(list);
      if (!selectedLower.has(data.name.toLowerCase())) onChange([...selected, data.name].join(', '));
      setNewName('');
      setAdding(false);
      if (data.store_error) setAddError(`Added here, but not on the store yet: ${data.store_error}`);
    } catch (err: any) {
      setAddError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const chip = (name: string, active: boolean, extra = false) => (
    <button
      key={name}
      type="button"
      onClick={() => toggle(name)}
      className={`px-3 py-1.5 rounded-full text-sm border min-h-[36px] touch-manipulation transition-colors ${
        active
          ? 'bg-blue-600 border-blue-600 text-white'
          : 'bg-white border-gray-300 text-gray-700 active:bg-gray-100'
      } ${extra ? 'border-dashed' : ''}`}
      title={extra ? 'Not a store category yet' : undefined}
    >
      {name}
    </button>
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      {loading ? (
        <p className="text-sm text-gray-500">Loading categories…</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {options.map((o) => chip(o.name, selectedLower.has(o.name.toLowerCase())))}
          {extras.map((name) => chip(name, true, true))}
          {isAdmin && !adding && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="px-3 py-1.5 rounded-full text-sm border border-gray-300 text-blue-700 bg-white min-h-[36px]"
            >
              + Add category
            </button>
          )}
        </div>
      )}
      {!loading && options.length === 0 && !isAdmin && (
        <p className="mt-1.5 text-xs text-gray-500">No categories yet — ask an admin to add some.</p>
      )}
      {adding && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCategory();
              }
            }}
            placeholder="New category name"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[40px]"
            autoFocus
          />
          <button type="button" onClick={addCategory} disabled={saving || !newName.trim()} className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm min-h-[40px] disabled:opacity-50">
            {saving ? 'Adding…' : 'Add'}
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewName(''); setAddError(''); }} className="px-3 py-2 rounded-md border border-gray-300 text-sm min-h-[40px]">
            Cancel
          </button>
        </div>
      )}
      {addError && <p className="mt-1.5 text-xs text-orange-700">{addError}</p>}
      {hint && !addError && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
};

export default CategoryPicker;
